import { API } from 'utils/api.config';
import { API_URL } from 'utils/api-url.config';
import { safeJSONParse } from 'utils/safeJwts';

const ENDPOINT = 'platform-analytics/api/v1/analytics';

function buildPayload(meta, playbackPosition, duration) {
  const { track } = meta;
  const isEpisode = track?.type === 'episode';
  return {
    contentId: String(track.id),
    contentName: track.title,
    seriesId: isEpisode && meta.seriesId != null ? String(meta.seriesId) : null,
    seasonId: isEpisode && meta.seasonId != null ? String(meta.seasonId) : null,
    platformName: 'web',
    contentType: track.type,
    isLive: !!track.live,
    playbackPosition,
    duration
  };
}

function send(payload, keepalive) {
  if (!keepalive) {
    API.post(ENDPOINT, payload).catch((err) => console.error('Playback analytics failed', err));
    return;
  }
  // Page is unloading: axios (XHR) would be cancelled, keepalive fetch survives it
  const title = window.platform_info.title;
  const token =
    safeJSONParse(localStorage.getItem(`access_token_r_${title}`)) ||
    safeJSONParse(localStorage.getItem(`access_token_temp_${title}`));
  fetch(`${API_URL}${ENDPOINT}`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload)
  }).catch(() => {});
}

/*
 *  Reports each continuously watched stretch of content.
 *  segStart = position where the current stretch began, lastPos = last position actually played.
 *  A stretch is reported on seek, end, source change (flush), dispose and page unload,
 *  with playbackPosition = lastPos and duration = lastPos - segStart (in seconds).
 */
export function attachPlaybackAnalytics(player, getMeta) {
  let segStart = null;
  let lastPos = null;

  const inAd = () => typeof player.ads?.inAdBreak === 'function' && player.ads.inAdBreak();

  function report(position, keepalive) {
    const start = segStart;
    segStart = null;
    lastPos = null;
    if (start === null || position == null || !Number.isFinite(position)) return;

    const meta = getMeta();
    const playbackPosition = Math.round(position);
    const duration = playbackPosition - Math.round(start);
    if (!meta?.track?.id || meta.isTrailer || duration <= 0) return;

    send(buildPayload(meta, playbackPosition, duration), keepalive);
  }

  // New source loaded: start clean so nothing from the previous content leaks in
  player.on('loadstart', () => {
    segStart = null;
    lastPos = null;
  });

  player.on('playing', () => {
    if (segStart === null && !inAd()) {
      segStart = lastPos = player.currentTime();
    }
  });

  player.on('timeupdate', () => {
    if (player.seeking() || inAd()) return;
    const time = player.currentTime();
    if (segStart === null) segStart = time;
    lastPos = time;
  });

  // currentTime() already points at the seek target here, so report the last played position
  player.on('seeking', () => {
    if (!inAd()) report(lastPos);
  });

  player.on('seeked', () => {
    if (!inAd()) segStart = lastPos = player.currentTime();
  });

  player.on('ended', () => {
    const duration = player.duration();
    report(Number.isFinite(duration) ? duration : lastPos);
  });

  const onPageHide = () => report(lastPos, true);
  window.addEventListener('pagehide', onPageHide);

  player.on('dispose', () => {
    report(lastPos);
    window.removeEventListener('pagehide', onPageHide);
  });

  return {
    flush: () => report(lastPos)
  };
}
